/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUsers,
  faBuilding,
  faCalendar,
  faChartBar,
  faCog,
  faCheckCircle,
  faTrophy,
  faArrowUp,
  faArrowDown,
  faUser,
  faUpload,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import InstituteDashboardHeader from '../components/InstituteDashboardHeader';
import StatCard from '../components/StatCard';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/InstituteDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const InstituteDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  // Track whether we've already done the initial dashboard load, to avoid
  // showing the full-screen loader on every small refresh (which can cause flicker).
  const hasLoadedOnceRef = useRef(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [traineesLoading, setTraineesLoading] = useState(false);
  const [traineesError, setTraineesError] = useState('');
  const [trainees, setTrainees] = useState([]);
  const [traineeSearch, setTraineeSearch] = useState('');
  const [traineeFilters, setTraineeFilters] = useState({
    department: '',
    year: '',
    status: ''
  });
  // Settings state (reuses generic /api/user/settings backend)
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    trainingAlerts: true,
    bulkUploadNotifications: true,
    eventReminders: true,
    weeklyDigest: false
  });
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'private',
    showEmail: false,
    showPhone: false,
    searchableByRecruiters: false,
    showLastActive: false,
    shareTraineeProfilesWithEmployers: true,
    allowExternalCompanyAccess: true,
    shareAnalyticsWithAdmin: true
  });
  const [displaySettings, setDisplaySettings] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'Africa/Nairobi'
  });
  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFilter, setEventFilter] = useState('all'); // all, upcoming, past
  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'Training Workshop',
    maxAttendees: '',
    organizer: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [dashboardData, setDashboardData] = useState({
    stats: {
        totalTrainees: 0,
      activeCompanies: 0,
      upcomingEvents: 0,
        activePrograms: 0,
        traineesCompleted: 0,
        activeTrainees: 0,
        completionRate: 0,
      activeInterns: 0
    },
    profileCompletion: 0,
    departments: [],
      recentEnrollments: [],
      trainees: [],
    companies: [],
    events: []
  });

  // Navigation items for Institute dashboard - dynamically computed with real trainee count
  const navigationItems = useMemo(() => {
    const totalTrainees = dashboardData.stats.totalTrainees || 0;
    return [
      { id: 'dashboard', icon: faChartLine, label: 'Dashboard', section: 'dashboard' },
      { 
        id: 'trainees', 
        icon: faUsers, 
        label: 'Trainees', 
        section: 'trainees', 
        badge: totalTrainees > 0 ? String(totalTrainees) : undefined
      },
      { id: 'bulk-upload', icon: faUpload, label: 'Bulk Upload', section: 'bulk-upload' },
      { id: 'companies', icon: faBuilding, label: 'Companies', section: 'companies' },
      { id: 'events', icon: faCalendar, label: 'Events', section: 'events' },
      { id: 'analytics', icon: faChartBar, label: 'Analytics', section: 'analytics' },
      { id: 'settings', icon: faCog, label: 'Settings', section: 'settings' },
      { id: 'myprofile', icon: faUser, label: 'My Profile', section: 'myprofile' }
    ];
  }, [dashboardData.stats.totalTrainees]);

  // Handle navigation click
  const handleNavClick = (item) => {
    if (item.section === 'myprofile') {
      navigate('/institute-my-profile');
      return;
    }
    setActiveSection(item.section);
  };

  // Get section title for header
  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'trainees': 'Trainees',
      'bulk-upload': 'Bulk Upload',
      'companies': 'Companies',
      'events': 'Events',
      'analytics': 'Analytics',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  // Fetch dashboard data (real stats from backend) - reusable function
  const fetchDashboardData = useCallback(async () => {
    try {
      // Only show the big full-screen loader the very first time the dashboard loads.
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      
      // Fetch stats
      const statsResponse = await axios.get(
        buildApiUrl('/api/institute/stats'),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch profile to calculate completion
      let profileCompletion = 0;
      try {
        const profileResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (profileResponse.data) {
          const profile = profileResponse.data;
          
          // Calculate profile completion based on institute-specific fields
          const requiredFields = [
            'instituteName',
            'institutionType',
            'registrationNumber',
            'officialEmail',
            'phoneNumber',
            'physicalAddress',
            'city',
            'county',
            'contactPersonName',
            'contactPersonTitle',
            'contactPersonPhone',
            'contactPersonEmail'
          ];
          
          // Count filled required fields
          const filledFields = requiredFields.filter(field => {
            const value = profile[field];
            return value && value.toString().trim() !== '';
          }).length;
          
          // Check for optional but important fields
          const optionalFields = [
            'websiteUrl',
            'totalStudentEnrollment',
            'numberOfPrograms',
            'careerCenterEmail',
            'careerOfficePhone',
            'areasOfSpecialization'
          ];
          
          const filledOptional = optionalFields.filter(field => {
            const value = profile[field];
            if (field === 'areasOfSpecialization') {
              return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
            }
            return value && value.toString().trim() !== '';
          }).length;
          
          // Calculate completion: 70% for required fields, 30% for optional
          const requiredPercentage = (filledFields / requiredFields.length) * 70;
          const optionalPercentage = (filledOptional / optionalFields.length) * 30;
          profileCompletion = Math.round(requiredPercentage + optionalPercentage);
          
          // If profile is marked as completed, ensure it shows 100%
          if (profile.profileCompleted || profile.hasCompletedProfile) {
            profileCompletion = 100;
          }
        }
      } catch (profileError) {
        console.error('Error fetching profile for completion:', profileError);
        // Continue with stats even if profile fetch fails
      }

      if (statsResponse.data?.success) {
        const stats = statsResponse.data.stats || {};
        const departments = statsResponse.data.departments || [];

        setDashboardData(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            totalTrainees: stats.totalTrainees || 0,
            activeCompanies: stats.companiesRecruiting || 0,
            upcomingEvents: stats.upcomingEvents || 0,
            activePrograms: stats.activePrograms || stats.numberOfPrograms || 0,
            traineesCompleted: stats.traineesCompleted || 0,
            activeTrainees: stats.activeTrainees || stats.totalTrainees || 0,
            completionRate: stats.completionRate || 0,
            activeJobSeekers: stats.activeJobSeekers || 0,
            averageSalary: stats.averageSalary || 0,
            activeInterns: stats.activeInterns || 0
          },
          departments,
          profileCompletion: Math.max(0, Math.min(100, profileCompletion))
        }));
      }

      setLoading(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch user-level settings when settings section is active
  useEffect(() => {
    const fetchSettings = async () => {
      if (activeSection !== 'settings') return;

      try {
        setSettingsLoading(true);
        setSettingsError('');
        setSettingsSuccess('');

        const token = localStorage.getItem('token');
        if (!token) {
          setSettingsError('You need to be logged in to view settings.');
          return;
        }

        const response = await axios.get(
          buildApiUrl('/api/user/settings'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          // Merge notification settings, preserving institute-specific fields
          if (response.data.notifications) {
            setNotificationSettings(prev => ({
              emailNotifications: response.data.notifications.emailNotifications ?? prev.emailNotifications,
              trainingAlerts: response.data.notifications.trainingAlerts ?? prev.trainingAlerts,
              bulkUploadNotifications: response.data.notifications.bulkUploadNotifications ?? prev.bulkUploadNotifications,
              eventReminders: response.data.notifications.eventReminders ?? prev.eventReminders,
              weeklyDigest: response.data.notifications.weeklyDigest ?? prev.weeklyDigest,
              // Also preserve any generic notification fields that might exist
              ...response.data.notifications
            }));
          }
          // Merge privacy settings, preserving institute-specific fields
          if (response.data.privacy) {
            setPrivacySettings(prev => ({
              ...prev,
              shareTraineeProfilesWithEmployers: response.data.privacy.shareTraineeProfilesWithEmployers ?? prev.shareTraineeProfilesWithEmployers,
              allowExternalCompanyAccess: response.data.privacy.allowExternalCompanyAccess ?? prev.allowExternalCompanyAccess,
              shareAnalyticsWithAdmin: response.data.privacy.shareAnalyticsWithAdmin ?? prev.shareAnalyticsWithAdmin,
              // Also preserve any generic privacy fields
              ...response.data.privacy
            }));
          }
          // Merge display settings
          if (response.data.display) {
            setDisplaySettings(prev => ({
              theme: response.data.display.theme ?? prev.theme,
              language: response.data.display.language ?? prev.language,
              timezone: response.data.display.timezone ?? prev.timezone
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load institute settings:', error);
        // Don't show error if it's just that settings don't exist yet (404)
        if (error.response?.status !== 404) {
          setSettingsError('Unable to load settings. Using default values.');
        }
      } finally {
        setSettingsLoading(false);
      }
    };

    fetchSettings();
  }, [activeSection]);

  // Fetch events when Events section is opened
  useEffect(() => {
    const fetchEvents = async () => {
      if (activeSection !== 'events') return;

      try {
        setEventsLoading(true);
        setEventsError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/institute/events'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setEvents(response.data.events || []);
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setEventsError('Failed to load events.');
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [activeSection]);

  // Fetch companies when Companies section is opened
  useEffect(() => {
    const fetchCompanies = async () => {
      if (activeSection !== 'companies') return;

      try {
        setCompaniesLoading(true);
        setCompaniesError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          buildApiUrl('/api/institute/companies'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.success) {
          setCompanies(response.data.companies || []);
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        setCompaniesError('Failed to load companies.');
      } finally {
        setCompaniesLoading(false);
      }
    };

    fetchCompanies();
  }, [activeSection]);

  // Fetch trainees - reusable function
  const fetchTrainees = useCallback(async (forceRefresh = false) => {
    // Only fetch when on trainees section, unless force refresh is requested
    if (!forceRefresh && activeSection !== 'trainees') return;

    try {
      setTraineesLoading(true);
      setTraineesError('');

      const token = localStorage.getItem('token');
      const params = {};
      if (traineeFilters.department) params.department = traineeFilters.department;
      if (traineeFilters.year) params.year = traineeFilters.year;
      if (traineeFilters.status) params.status = traineeFilters.status;

      const response = await axios.get(
        buildApiUrl('/api/institute/trainees'),
        {
          headers: { Authorization: `Bearer ${token}` },
          params
        }
      );

      if (response.data?.success) {
        setTrainees(response.data.trainees || []);
      } else {
        setTrainees([]);
        setTraineesError('Unable to load trainees right now.');
      }
    } catch (error) {
      console.error('Error fetching institute trainees:', error);
      setTrainees([]);
      setTraineesError(error.response?.data?.error || 'Failed to fetch trainees.');
    } finally {
      setTraineesLoading(false);
    }
  }, [activeSection, traineeFilters.department, traineeFilters.year, traineeFilters.status]);

  // Fetch trainees when Trainees section is opened or filters change
  useEffect(() => {
    fetchTrainees();
  }, [fetchTrainees]);

  // Render Dashboard section
  const renderDashboard = () => (
    <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard
          title="Total Trainees"
          value={dashboardData.stats.totalTrainees}
          icon={faUsers}
          iconColor="#f97316"
        />
        <StatCard
          title="Active Companies"
          value={dashboardData.stats.activeCompanies}
          icon={faBuilding}
          iconColor="#0d9488"
        />
        <StatCard
          title="Upcoming Events"
          value={dashboardData.stats.upcomingEvents}
          icon={faCalendar}
          iconColor="#3b82f6"
        />
        <StatCard
          title="Active Programs"
          value={dashboardData.stats.activePrograms || 0}
          icon={faTrophy}
          iconColor="#10b981"
        />
      </div>

      {/* Program-wise Enrollment */}
      <div className="section-card">
        <h2><FontAwesomeIcon icon={faChartBar} /> Program-wise Enrollment</h2>
        <div className="departments-list">
          {dashboardData.departments.map((dept, idx) => (
            <div key={idx} className="department-item">
              <div className="department-header">
                <h4>{dept.name}</h4>
                <span className="placement-rate">{dept.enrolled || dept.total || 0} trainees</span>
              </div>
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.min(100, ((dept.enrolled || dept.total || 0) / 100) * 100)}%` }}></div>
                </div>
                <span className="progress-label">{dept.enrolled || dept.total || 0} Enrolled Trainees</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Enrollments */}
      <div className="section-card">
        <h2><FontAwesomeIcon icon={faUsers} /> Recent Enrollments</h2>
        <div className="placements-list">
          {dashboardData.recentEnrollments && dashboardData.recentEnrollments.length > 0 ? (
            dashboardData.recentEnrollments.slice(0, 5).map((trainee, idx) => (
            <div key={idx} className="placement-item">
              <div className="placement-avatar">
                  {trainee.name.charAt(0)}
              </div>
              <div className="placement-info">
                  <h4>{trainee.name}</h4>
                  <p>{trainee.department || trainee.program} • {trainee.batch || 'Current Batch'}</p>
              </div>
              <div className="placement-badge">
                  <FontAwesomeIcon icon={faCheckCircle} /> Enrolled
              </div>
            </div>
            ))
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              <p>No recent enrollments to display</p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>New trainee enrollments will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render other sections
  const handleBulkTraineesUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    // Use a simple, human-friendly label so Super Admin can identify the batch
    const defaultLabel = `Cohort Upload - ${new Date().getFullYear()}`;
    formData.append('file', file);
    formData.append('cohortLabel', defaultLabel);

    try {
      setBulkUploading(true);
      setBulkUploadResult(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        buildApiUrl('/api/institute/bulk-trainees/upload'),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setBulkUploadResult({
        type: 'success',
        message: response.data?.message || 'Trainee file uploaded successfully.'
      });
      // Reset the input so the same file can be selected again if needed
      event.target.value = '';
      
      // Refresh dashboard stats and trainees list to show newly created trainees
      await fetchDashboardData();
      // Refresh the trainees list (force refresh even if not on trainees section)
      await fetchTrainees(true);
    } catch (error) {
      console.error('Bulk trainee upload failed:', error);
      setBulkUploadResult({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Upload failed'
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadTraineeTemplate = () => {
    // Use the dedicated institute trainee template for institute uploads
    const url = buildApiUrl('/api/bulk-import/template/institute_trainee');
    window.open(url, '_blank', 'noopener');
  };

  const renderTrainees = () => {
    // Derive quick stats from current trainees list
    const total = trainees.length;
    const enrolled = trainees.filter(s => {
      const status = (s.status || '').toLowerCase();
      return ['enrolled', 'in training', 'active'].includes(status) || !status;
    }).length;
    const completed = trainees.filter(s => (s.status || '').toLowerCase() === 'completed' || (s.status || '').toLowerCase() === 'graduated').length;
    const inTraining = trainees.filter(s => {
      const status = (s.status || '').toLowerCase();
      return ['in training', 'active', 'enrolled'].includes(status);
    }).length;

    // Faculty-wise (department-wise) distribution
    const facultyMap = new Map();
    trainees.forEach((s) => {
      const facultyKey = (s.faculty || s.department || 'Not specified') || 'Not specified';
      if (!facultyMap.has(facultyKey)) {
        facultyMap.set(facultyKey, {
          name: facultyKey,
          total: 0,
          enrolled: 0,
          completed: 0,
          active: 0
        });
      }
      const entry = facultyMap.get(facultyKey);
      entry.total += 1;
      const status = (s.status || '').toLowerCase();
      if (['enrolled', 'in training', 'active'].includes(status) || !status) {
        entry.enrolled += 1;
        entry.active += 1;
      }
      if (['completed', 'graduated'].includes(status)) entry.completed += 1;
    });
    const facultyStats = Array.from(facultyMap.values()).sort((a, b) => b.total - a.total);

    const uniqueDepartments = Array.from(
      new Set(trainees.map(s => s.department).filter(Boolean))
    );
    const uniqueYears = Array.from(
      new Set(trainees.map(s => s.year).filter(Boolean))
    );

    const filteredTrainees = trainees.filter((trainee) => {
      if (!traineeSearch.trim()) return true;
      const q = traineeSearch.toLowerCase();
      return (
        (trainee.name || '').toLowerCase().includes(q) ||
        (trainee.email || '').toLowerCase().includes(q) ||
        (trainee.department || '').toLowerCase().includes(q)
      );
    });

    const getStatusClass = (status) => {
      const value = (status || '').toLowerCase();
      if (value === 'completed' || value === 'graduated') return 'status-badge status-offered';
      if (value === 'in training' || value === 'active') return 'status-badge status-interview';
      if (value === 'enrolled') return 'status-badge status-applied';
      return 'status-badge status-pending';
    };

    return (
    <div className="section-card">
      <h2>Trainees Management</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          View and manage all your trainees in one place. Track enrollment status, training progress,
          and completion rates across all programs.
        </p>

        {/* Quick stats row (without Active Job Seekers card) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Trainees
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              In Training
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{inTraining}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Completed
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{completed}</div>
          </div>
        </div>

        {/* Program / department-wise trainee distribution */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Trainee distribution by program / department
          </h3>
          {facultyStats.length === 0 ? (
            <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
              Once trainees are added, you'll see how they are distributed across your programs and departments here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {facultyStats.map((faculty) => {
                const enrollmentRate =
                  faculty.total > 0 ? Math.round((faculty.enrolled / faculty.total) * 100) : 0;
                return (
                  <div
                    key={faculty.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={{ minWidth: '200px' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>
                        {faculty.name}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {faculty.total} trainees • {faculty.enrolled} enrolled • {faculty.completed} completed
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: '10px',
                        borderRadius: '999px',
                        background: '#e5e7eb',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          width: `${enrollmentRate}%`,
                          height: '100%',
                          background:
                            'linear-gradient(90deg, rgba(16,185,129,1) 0%, rgba(59,130,246,1) 100%)',
                          transition: 'width 0.4s ease'
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: '52px',
                        textAlign: 'right',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#0f172a'
                      }}
                    >
                      {enrollmentRate}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filters + search + bulk upload actions */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '1.25rem'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Department
              </label>
              <select
                className="filter-select"
                value={traineeFilters.department}
                onChange={(e) => setTraineeFilters(prev => ({ ...prev, department: e.target.value }))}
                style={{ minWidth: '160px' }}
              >
                <option value="">All</option>
                {uniqueDepartments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Year
              </label>
              <select
                className="filter-select"
                value={traineeFilters.year}
                onChange={(e) => setTraineeFilters(prev => ({ ...prev, year: e.target.value }))}
                style={{ minWidth: '120px' }}
              >
                <option value="">All</option>
                {uniqueYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  marginBottom: '0.25rem'
                }}
              >
                Status
              </label>
              <select
                className="filter-select"
                value={traineeFilters.status}
                onChange={(e) => setTraineeFilters(prev => ({ ...prev, status: e.target.value }))}
                style={{ minWidth: '160px' }}
              >
                <option value="">All</option>
                <option value="Enrolled">Enrolled</option>
                <option value="In Training">In Training</option>
                <option value="Completed">Completed</option>
                <option value="Graduated">Graduated</option>
                <option value="Active">Active</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '260px', maxWidth: '360px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#6b7280'
              }}
            >
              Search by name, email, or department
            </label>
            <input
              type="text"
              value={traineeSearch}
              onChange={(e) => setTraineeSearch(e.target.value)}
              placeholder="e.g. Computer Science, Jane, @institute.ac.ke"
              style={{
                padding: '0.7rem 0.9rem',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '0.9rem',
                outline: 'none'
              }}
            />
          </div>
        </div>

        {/* Trainees table */}
        <div className="table-container">
          {traineesLoading && (
            <div className="empty-state">
              <div className="empty-icon">🎓</div>
              <h3>Loading trainees...</h3>
              <p>Please wait while we fetch the latest trainee list for your institute.</p>
            </div>
          )}

          {!traineesLoading && traineesError && (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <h3>Unable to load trainees</h3>
              <p>{traineesError}</p>
            </div>
          )}

          {!traineesLoading && !traineesError && filteredTrainees.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🎓</div>
              <h3>No trainees match your filters yet</h3>
              <p>Try adjusting the filters or clearing the search.</p>
            </div>
          )}

          {!traineesLoading && !traineesError && filteredTrainees.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Trainee</th>
                  <th>Email</th>
                  <th>Program</th>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Enrollment Date</th>
                  <th>Skills</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrainees.map((trainee) => (
                  <tr key={trainee.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.1rem' }}>
                        {trainee.name || 'Unnamed'}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {trainee.userType === 'intern' ? 'Intern' : 'Trainee'}
                      </div>
                    </td>
                    <td>{trainee.email}</td>
                    <td>{trainee.department || trainee.program || 'N/A'}</td>
                    <td>{trainee.year || trainee.batch || 'N/A'}</td>
                    <td>
                      <span className={getStatusClass(trainee.status)}>
                        {trainee.status || 'Enrolled'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.8rem', color: '#0f172a' }}>
                        {trainee.progress || trainee.completionPercentage || 0}%
                      </div>
                      {trainee.progress && (
                        <div style={{ 
                          width: '60px', 
                          height: '4px', 
                          background: '#e5e7eb', 
                          borderRadius: '2px',
                          marginTop: '4px'
                        }}>
                          <div style={{ 
                            width: `${trainee.progress}%`, 
                            height: '100%', 
                            background: '#3b82f6',
                            borderRadius: '2px'
                          }}></div>
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.8rem', color: '#0f172a' }}>
                        {trainee.enrollmentDate || trainee.startDate || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="skill-tags">
                        {(trainee.skills || []).slice(0, 3).map((skill, idx) => (
                          <span key={idx}>{skill}</span>
                        ))}
                        {(trainee.skills || []).length > 3 && (
                          <span>+{trainee.skills.length - 3} more</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
    </div>
  );
  };

  const renderCompanies = () => {
    const totalTraineesHired = companies.reduce((sum, company) => sum + (company.traineesHired || 0), 0);
    const totalActiveInternships = companies.reduce((sum, company) => sum + (company.activeInternships || 0), 0);

    return (
      <div className="section-card">
        <h2>Partner Companies</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          Companies that hire from your institute. These are partner organizations that recruit
          graduates and trainees from your training programs through the Akshar Jobs platform.
        </p>

        {companiesError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {companiesError}
          </div>
        )}

        {/* Summary Stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Companies
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{companies.length}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Graduates Hired
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{totalTraineesHired}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Active Internships Postings
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{totalActiveInternships}</div>
          </div>
        </div>

        {/* Companies List */}
        {companiesLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="institute" />
            <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No companies yet</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Companies will appear here once your graduates are hired through the platform.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
            {companies.map((company) => (
              <div
                key={company.id}
                style={{
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedCompany(company)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      display: company.logo ? 'none' : 'flex',
                      width: '56px',
                      height: '56px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(13, 148, 136, 0.1) 100%)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#f97316'
                    }}
                  >
                    {company.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      color: '#0f172a',
                      margin: 0,
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {company.name}
                    </h3>
                    {company.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: 'rgba(249, 115, 22, 0.1)',
                        color: '#f97316'
                      }}>
                        {company.category}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ color: '#10b981' }} />
                    <span><strong style={{ color: '#0f172a' }}>{company.traineesHired || 0}</strong> graduate{company.traineesHired !== 1 ? 's' : ''} hired</span>
                  </div>
                  {company.activeInternships > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                      <FontAwesomeIcon icon={faBuilding} style={{ color: '#3b82f6' }} />
                      <span><strong style={{ color: '#0f172a' }}>{company.activeInternships}</strong> active internship{company.activeInternships !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {company.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#64748b' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '0.75rem' }} />
                      <span>{company.location}</span>
                    </div>
                  )}
                </div>

                {company.trainees && company.trainees.length > 0 && (
                  <div style={{
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    color: '#64748b'
                  }}>
                    <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                      Graduates Hired:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      {company.trainees.slice(0, 3).map((trainee, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(13, 148, 136, 0.2) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: '#f97316'
                          }}>
                            {trainee.name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {trainee.name}
                          </span>
                          {trainee.department && (
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {trainee.department}
                            </span>
                          )}
                        </div>
                      ))}
                      {company.trainees.length > 3 && (
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          +{company.trainees.length - 3} more trainee{company.trainees.length - 3 !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Company Detail Modal */}
        {selectedCompany && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedCompany(null)}
          >
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  {selectedCompany.name}
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedCompany(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {selectedCompany.logo && (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={selectedCompany.logo}
                      alt={selectedCompany.name}
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '12px',
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0'
                      }}
                    />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Graduates Hired</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>
                      {selectedCompany.traineesHired || selectedCompany.studentsHired || 0}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Active Internships</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>
                      {selectedCompany.activeInternships || 0}
                    </div>
                  </div>
                </div>

                {selectedCompany.category && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Industry</div>
                    <div style={{ fontSize: '0.95rem', color: '#0f172a', fontWeight: 600 }}>{selectedCompany.category}</div>
                  </div>
                )}

                {selectedCompany.location && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Location</div>
                    <div style={{ fontSize: '0.95rem', color: '#0f172a' }}>{selectedCompany.location}</div>
                  </div>
                )}

                {selectedCompany.website && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Website</div>
                    <a
                      href={selectedCompany.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.95rem', color: '#3b82f6', textDecoration: 'none' }}
                    >
                      {selectedCompany.website}
                    </a>
                  </div>
                )}

                {(selectedCompany.trainees || selectedCompany.students) && (selectedCompany.trainees?.length > 0 || selectedCompany.students?.length > 0) && (
                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem', fontWeight: 600 }}>
                      Graduates Hired ({(selectedCompany.trainees || selectedCompany.students || []).length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
                      {(selectedCompany.trainees || selectedCompany.students || []).map((trainee, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '0.75rem',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: '#f9fafb'
                          }}
                        >
                          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                            {trainee.name}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {trainee.department || trainee.program || 'Program not specified'}
                          </div>
                          {trainee.email && (
                            <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                              {trainee.email}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleEventFormChange = (field, value) => {
    setEventForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        buildApiUrl('/api/institute/events'),
        eventForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/institute/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
      
      // Reset form
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        type: 'Training Workshop',
        maxAttendees: '',
        organizer: '',
        contactEmail: '',
        contactPhone: ''
      });
      setShowEventForm(false);
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  const handleUpdateEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        buildApiUrl(`/api/institute/events/${editingEvent.id}`),
        eventForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/institute/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
      
      // Reset form
      setEditingEvent(null);
      setEventForm({
        title: '',
        description: '',
        date: '',
        time: '',
        location: '',
        type: 'Training Workshop',
        maxAttendees: '',
        organizer: '',
        contactEmail: '',
        contactPhone: ''
      });
      setShowEventForm(false);
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Failed to update event. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        buildApiUrl(`/api/institute/events/${eventId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh events list
      const response = await axios.get(
        buildApiUrl('/api/institute/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? event.date.split('T')[0] : '',
      time: event.time || '',
      location: event.location || '',
      type: event.type || 'Campus Drive',
      maxAttendees: event.maxAttendees || '',
      organizer: event.organizer || '',
      contactEmail: event.contactEmail || '',
      contactPhone: event.contactPhone || ''
    });
    setShowEventForm(true);
  };

  const renderEvents = () => {
    const now = new Date();
    const filteredEvents = events.filter(event => {
      if (eventFilter === 'all') return true;
      if (!event.date) return false;
      const eventDate = new Date(event.date);
      if (eventFilter === 'upcoming') return eventDate >= now;
      if (eventFilter === 'past') return eventDate < now;
      return true;
    });

    const eventTypes = ['Training Workshop', 'Career Workshop', 'Company Visit', 'Networking Event', 'Graduation Ceremony', 'General'];

    return (
      <div className="section-card">
        <div style={{ marginBottom: '1.5rem' }}>
          <h2>Events Management</h2>
          <p style={{ marginTop: '0.5rem', color: '#64748b', maxWidth: '720px', marginBottom: '1rem' }}>
            Manage training workshops, career events, and networking activities for your institute.
          </p>
          
          {/* All buttons in one horizontal row */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              style={{
                flex: 1,
                fontSize: '0.9rem',
                padding: '0.6rem 1.2rem',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                setEditingEvent(null);
                setEventForm({
                  title: '',
                  description: '',
                  date: '',
                  time: '',
                  location: '',
                  type: 'Training Workshop',
                  maxAttendees: '',
                  organizer: '',
                  contactEmail: '',
                  contactPhone: ''
                });
                setShowEventForm(true);
              }}
            >
              <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '0.5rem' }} />
              Create New Event
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('all')}
              className={eventFilter === 'all' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              All Events ({events.length})
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('upcoming')}
              className={eventFilter === 'upcoming' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              Upcoming ({events.filter(e => e.date && new Date(e.date) >= now).length})
            </button>
            <button
              type="button"
              onClick={() => setEventFilter('past')}
              className={eventFilter === 'past' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ flex: 1, fontSize: '0.9rem', padding: '0.6rem 1.2rem', whiteSpace: 'nowrap' }}
            >
              Past ({events.filter(e => e.date && new Date(e.date) < now).length})
            </button>
          </div>
        </div>

        {eventsError && (
          <div style={{
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}>
            {eventsError}
          </div>
        )}

        {/* Event Form Modal - Completely redesigned for stability */}
        {showEventForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem 1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEventForm(false);
                setEditingEvent(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '600px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Header - Fixed */}
              <div style={{ 
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0.25rem',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Scrollable Content */}
              <div style={{ 
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1,
                minHeight: 0
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Event Title *
                    </label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => handleEventFormChange('title', e.target.value)}
                      placeholder="e.g. Tech Career Fair 2024"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Description
                    </label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => handleEventFormChange('description', e.target.value)}
                      placeholder="Event description and details..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Date *
                      </label>
                      <input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => handleEventFormChange('date', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Time
                      </label>
                      <input
                        type="time"
                        value={eventForm.time}
                        onChange={(e) => handleEventFormChange('time', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={eventForm.location}
                      onChange={(e) => handleEventFormChange('location', e.target.value)}
                      placeholder="e.g. Main Auditorium, Building A"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Event Type
                      </label>
                      <select
                        value={eventForm.type}
                        onChange={(e) => handleEventFormChange('type', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {eventTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Max Attendees
                      </label>
                      <input
                        type="number"
                        value={eventForm.maxAttendees}
                        onChange={(e) => handleEventFormChange('maxAttendees', e.target.value)}
                        placeholder="0 for unlimited"
                        min="0"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                      Organizer
                    </label>
                    <input
                      type="text"
                      value={eventForm.organizer}
                      onChange={(e) => handleEventFormChange('organizer', e.target.value)}
                      placeholder="e.g. Training Office"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={eventForm.contactEmail}
                        onChange={(e) => handleEventFormChange('contactEmail', e.target.value)}
                        placeholder="contact@institute.ac.ke"
                        style={{
                          width: '100%',
                          padding: '0.625rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          fontSize: '0.875rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
                        Contact Phone
                      </label>
                    <input
                      type="tel"
                      value={eventForm.contactPhone}
                      onChange={(e) => handleEventFormChange('contactPhone', e.target.value)}
                      placeholder="+254 700 000 000"
                      style={{
                        width: '100%',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem'
                      }}
                    />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div style={{ 
                padding: '1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                flexShrink: 0
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEventForm(false);
                    setEditingEvent(null);
                  }}
                  style={{ padding: '0.625rem 1.25rem' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                  disabled={!eventForm.title || !eventForm.date}
                  style={{ padding: '0.625rem 1.25rem' }}
                >
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Events List */}
        {eventsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <ThemedLoadingSpinner theme="institute" />
            <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0'
          }}>
            <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '1rem' }} />
            <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '0.5rem' }}>No events found</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              {eventFilter === 'all' 
                ? 'Create your first event to get started.'
                : `No ${eventFilter} events. Try selecting a different filter.`}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredEvents.map((event) => {
              const eventDate = event.date ? new Date(event.date) : null;
              const isUpcoming = eventDate && eventDate >= now;
              const isPast = eventDate && eventDate < now;

              return (
                <div
                  key={event.id}
                  style={{
                    padding: '1.25rem 1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    background: isPast ? '#f9fafb' : '#ffffff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1.5rem'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                        {event.title}
                      </h3>
                      <span style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: event.type === 'Campus Drive' ? 'rgba(249, 115, 22, 0.1)' : 
                                   event.type === 'Career Workshop' ? 'rgba(59, 130, 246, 0.1)' :
                                   event.type === 'Training Workshop' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(156, 163, 175, 0.1)',
                        color: event.type === 'Campus Drive' ? '#f97316' : 
                               event.type === 'Career Workshop' ? '#3b82f6' :
                               event.type === 'Training Workshop' ? '#10b981' : '#6b7280'
                      }}>
                        {event.type}
                      </span>
                      {isUpcoming && (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'rgba(16, 185, 129, 0.1)',
                          color: '#10b981'
                        }}>
                          Upcoming
                        </span>
                      )}
                      {isPast && (
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: 'rgba(156, 163, 175, 0.1)',
                          color: '#6b7280'
                        }}>
                          Past
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        {event.description.length > 150 ? `${event.description.substring(0, 150)}...` : event.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#64748b' }}>
                      {eventDate && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faCalendar} />
                          <span>{eventDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          {event.time && <span>at {event.time}</span>}
                        </div>
                      )}
                      {event.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faBuilding} />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {event.maxAttendees > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <FontAwesomeIcon icon={faUsers} />
                          <span>{event.attendees || 0} / {event.maxAttendees} attendees</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleEditEvent(event)}
                      style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleDeleteEvent(event.id)}
                      style={{ 
                        fontSize: '0.85rem', 
                        padding: '0.5rem 0.75rem',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: '1px solid #fecaca'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAnalytics = () => {
    const stats = dashboardData.stats || {};

    // Basic training numbers
    const total = stats.totalTrainees || 0;
    const completed = stats.traineesCompleted || 0;
    const activeTrainees = stats.activeTrainees || 0;
    const enrolled = total - completed;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Chart data for Training Completion Rate (Bar Chart)
    const completionRateChartData = {
      labels: ['Completion Rate'],
      datasets: [
        {
          label: 'Completed',
          data: [completionRate],
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2
        },
        {
          label: 'In Training',
          data: [100 - completionRate],
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2
        }
      ]
    };

    const completionRateChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    // Chart data for Trainee Status Distribution (Doughnut Chart)
    const traineeStatusChartData = {
      labels: ['Completed', 'In Training', 'Enrolled', 'Not Active'],
      datasets: [
        {
          data: [
            completed,
            activeTrainees,
            enrolled - activeTrainees,
            Math.max(0, total - completed - enrolled)
          ],
          backgroundColor: [
            'rgba(16, 185, 129, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(249, 115, 22, 0.8)',
            'rgba(156, 163, 175, 0.8)'
          ],
          borderColor: [
            'rgba(16, 185, 129, 1)',
            'rgba(249, 115, 22, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(156, 163, 175, 1)'
          ],
          borderWidth: 2
        }
      ]
    };

    const traineeStatusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Chart data for Completion Rate Distribution (Bar Chart)
    const distributionRanges = ['0-25%', '26-50%', '51-75%', '76-100%'];
    const distributionValues = [
      completionRate <= 25 ? completionRate : 0,
      completionRate > 25 && completionRate <= 50 ? completionRate - 25 : completionRate > 50 ? 25 : 0,
      completionRate > 50 && completionRate <= 75 ? completionRate - 50 : completionRate > 75 ? 25 : 0,
      completionRate > 75 ? completionRate - 75 : 0
    ];

    const distributionChartData = {
      labels: distributionRanges,
      datasets: [
        {
          label: 'Completion Rate %',
          data: distributionValues,
          backgroundColor: [
            'rgba(59, 130, 246, 0.8)',
            'rgba(56, 189, 248, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(34, 197, 94, 0.8)'
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(56, 189, 248, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(34, 197, 94, 1)'
          ],
          borderWidth: 2
        }
      ]
    };

    const distributionChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.parsed.y}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    return (
      <div className="section-card">
        <h2>Analytics</h2>
        <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
          High-level analytics for your training programs. These charts use the same live data that powers the
          Trainees Management section and dashboard stats.
        </p>

        {/* Top metrics row (without Active Job Seekers card) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}
        >
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Total Trainees
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              Completed Training
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{completed}</div>
          </div>
          <div className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
              In Training
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#3b82f6' }}>{activeTrainees}</div>
          </div>
        </div>

        {/* Overall Completion Rate Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Training Completion Rate
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>
                No training data yet. This chart will update automatically as trainees complete their programs.
              </p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Bar data={completionRateChartData} options={completionRateChartOptions} />
            </div>
          )}
        </div>

        {/* Trainee Status Distribution Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem',
            marginBottom: '1.5rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Trainee Status Distribution
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>No trainee data yet.</p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Doughnut data={traineeStatusChartData} options={traineeStatusChartOptions} />
            </div>
          )}
        </div>

        {/* Completion Rate Distribution Chart */}
        <div
          className="section-card"
          style={{
            padding: '1.5rem 1.75rem'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
            Completion Rate Distribution
          </h3>
          {total === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <p style={{ fontSize: '0.9rem', margin: 0 }}>No training data yet.</p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Bar data={distributionChartData} options={distributionChartOptions} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBulkUpload = () => (
    <div className="section-card">
      <h2>Bulk Upload Trainees</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Upload trainee cohorts in bulk using a CSV or Excel file. Download the template, fill in your cohort details,
        and upload here. Trainees will be created immediately and visible in the Trainees section. Super Admin can send welcome emails from the bulk-import dashboard if needed.
      </p>

      <div
        style={{
          padding: '1.5rem 1.75rem',
          borderRadius: '16px',
          border: '1px dashed rgba(148, 163, 184, 0.9)',
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.03) 0%, rgba(13, 148, 136, 0.03) 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.9rem'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.2rem' }}>
              Bulk upload new trainees
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', maxWidth: '500px' }}>
              Download the CSV template, fill in your cohort details, and upload here. Trainees will be created immediately and visible in the Trainees section.
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '220px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleDownloadTraineeTemplate}
            >
              Download Trainee Template (CSV)
            </button>
            <input
              id="bulk-trainees-file"
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={handleBulkTraineesUpload}
              disabled={bulkUploading}
            />
            {bulkUploading && (
              <span style={{ fontSize: '0.85rem', color: '#0d9488', fontWeight: 600 }}>
                Uploading trainee file...
              </span>
            )}
          </div>
        </div>

        {bulkUploadResult && (
          <div
            style={{
              marginTop: '0.25rem',
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              border: `1px solid ${
                bulkUploadResult.type === 'success' ? 'rgba(16, 185, 129, 0.6)' : 'rgba(248, 113, 113, 0.7)'
              }`,
              background:
                bulkUploadResult.type === 'success'
                  ? 'rgba(16, 185, 129, 0.06)'
                  : 'rgba(248, 113, 113, 0.06)',
              color: bulkUploadResult.type === 'success' ? '#065f46' : '#991b1b',
              fontSize: '0.9rem',
              fontWeight: 500
            }}
          >
            {bulkUploadResult.message}
          </div>
        )}
      </div>
    </div>
  );

  const handleToggleNotification = (key) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleTogglePrivacy = (key) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDisplayChange = (field, value) => {
    setDisplaySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsSaving(true);
      setSettingsError('');
      setSettingsSuccess('');

      const token = localStorage.getItem('token');
      if (!token) {
        setSettingsError('You need to be logged in to update settings.');
        setSettingsSaving(false);
        return;
      }

      const response = await axios.put(
        buildApiUrl('/api/user/settings'),
        {
          notifications: notificationSettings,
          privacy: privacySettings,
          display: displaySettings
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      if (response.data && response.data.message) {
        setSettingsSuccess(response.data.message || 'Settings saved successfully.');
      } else {
        setSettingsSuccess('Settings saved successfully.');
      }

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSettingsSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Failed to save institute settings:', error);
      const errorMessage = error.response?.data?.error || 
                          error.message || 
                          'Failed to save settings. Please try again.';
      setSettingsError(errorMessage);
      
      // Auto-clear error message after 8 seconds
      setTimeout(() => {
        setSettingsError('');
      }, 8000);
    } finally {
      setSettingsSaving(false);
    }
  };

  const renderSettings = () => (
    <div className="section-card">
      <h2>Institute Settings</h2>
      <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
        Configure how your institute uses Akshar Jobs. These settings control training notifications,
        how employers can interact with your trainees, and basic display preferences for your team.
      </p>

      {settingsLoading && (
        <div style={{ 
          marginBottom: '1rem', 
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '0.9rem', 
          color: '#64748b',
          background: 'rgba(249, 115, 22, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(249, 115, 22, 0.2)'
        }}>
          <ThemedLoadingSpinner theme="institute" />
          <span>Loading current settings...</span>
        </div>
      )}
      {settingsError && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.7)',
            background: 'rgba(248, 113, 113, 0.06)',
            color: '#991b1b',
            fontSize: '0.9rem'
          }}
        >
          {settingsError}
        </div>
      )}
      {settingsSuccess && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid rgba(16, 185, 129, 0.7)',
            background: 'rgba(16, 185, 129, 0.06)',
            color: '#065f46',
            fontSize: '0.9rem'
          }}
        >
          {settingsSuccess}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: '1.5rem'
        }}
      >
        {/* Left column: Notifications + Privacy */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div
            className="section-card"
            style={{ padding: '1.25rem 1.5rem', marginBottom: 0 }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
              Training & Notification Preferences
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Control which emails and alerts your training office receives.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Email notifications for new employer job postings targeting your institute
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.emailNotifications}
                  onChange={() => handleToggleNotification('emailNotifications')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Daily summary of training activity (enrollments, completions, program updates)
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.trainingAlerts}
                  onChange={() => handleToggleNotification('trainingAlerts')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Email when a bulk student upload has been processed by Super Admin
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.bulkUploadNotifications}
                  onChange={() => handleToggleNotification('bulkUploadNotifications')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>
                  Reminders for upcoming campus events, drives, and career workshops
                </span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.eventReminders}
                  onChange={() => handleToggleNotification('eventReminders')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Weekly digest email with key stats and trends</span>
                <input
                  type="checkbox"
                  checked={!!notificationSettings.weeklyDigest}
                  onChange={() => handleToggleNotification('weeklyDigest')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
            </div>
          </div>

          <div
            className="section-card"
            style={{ padding: '1.25rem 1.5rem', marginBottom: 0 }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
              Employer & Data Access
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
              Decide how much access employers and Akshar Jobs admins have to your student data.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Allow verified employers to view trainee profiles shared by your training team</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.shareTraineeProfilesWithEmployers}
                  onChange={() => handleTogglePrivacy('shareTraineeProfilesWithEmployers')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Allow external company accounts to download shared CVs</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.allowExternalCompanyAccess}
                  onChange={() => handleTogglePrivacy('allowExternalCompanyAccess')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                fontSize: '0.9rem', 
                color: '#0f172a',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(249, 115, 22, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <span style={{ flex: 1, marginRight: '1rem' }}>Share anonymised training analytics with Akshar Jobs Super Admin</span>
                <input
                  type="checkbox"
                  checked={!!privacySettings.shareAnalyticsWithAdmin}
                  onChange={() => handleTogglePrivacy('shareAnalyticsWithAdmin')}
                  disabled={settingsSaving || settingsLoading}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                    accentColor: '#f97316',
                    opacity: (settingsSaving || settingsLoading) ? 0.6 : 1
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Right column: Display / Localization */}
        <div
          className="section-card"
          style={{ padding: '1.25rem 1.5rem', alignSelf: 'flex-start' }}
        >
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>
            Display & Localization
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            Set defaults for language and time zone used across your institute dashboard.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label
                htmlFor="institute-theme"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Theme
              </label>
              <select
                id="institute-theme"
                value={displaySettings.theme}
                onChange={(e) => handleDisplayChange('theme', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System default</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="institute-language"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Language
              </label>
              <select
                id="institute-language"
                value={displaySettings.language}
                onChange={(e) => handleDisplayChange('language', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="en">English</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="institute-timezone"
                style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#0f172a' }}
              >
                Time zone
              </label>
              <select
                id="institute-timezone"
                value={displaySettings.timezone}
                onChange={(e) => handleDisplayChange('timezone', e.target.value)}
                disabled={settingsSaving || settingsLoading}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.9rem',
                  cursor: (settingsSaving || settingsLoading) ? 'not-allowed' : 'pointer',
                  opacity: (settingsSaving || settingsLoading) ? 0.6 : 1,
                  backgroundColor: (settingsSaving || settingsLoading) ? '#f3f4f6' : '#ffffff'
                }}
              >
                <option value="Africa/Nairobi">Africa / Nairobi (EAT)</option>
                <option value="UTC">UTC</option>
                <option value="Asia/Kolkata">Asia / Kolkata (IST)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem'
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          disabled={settingsSaving}
          onClick={() => {
            // Reset messages, but keep current values
            setSettingsError('');
            setSettingsSuccess('');
          }}
        >
          Clear Messages
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveSettings}
          disabled={settingsSaving}
        >
          {settingsSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="institute-dashboard-container">
        <div className="loading-container">
          <ThemedLoadingSpinner theme="institute" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Institute Dashboard Header - Full Width */}
      <InstituteDashboardHeader 
        currentPage={getSectionTitle()}
        onSearch={(term) => console.log('Search:', term)}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <div className="dashboard-container institute-dashboard-container" style={{ marginTop: '0' }}>
        {/* Sidebar */}
        <div 
          className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`} 
          style={{ top: '80px', height: 'calc(100vh - 80px)', paddingTop: '20px' }}
          onClick={(e) => {
            if (window.innerWidth <= 768 && e.target.closest('.nav-item')) {
              setSidebarOpen(false);
            }
          }}
        >
          <div className="nav-menu">
            {navigationItems.map((item) => (
              <div 
                key={item.id}
                className={`nav-item ${activeSection === item.section ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && (
                  <span className="badge">
                    {item.badge}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Profile Completion Card */}
          <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
              Profile Complete
            </div>
            <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
              {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div className="content-area">
            {/* Render sections based on activeSection */}
            {activeSection === 'dashboard' && renderDashboard()}
            {activeSection === 'trainees' && renderTrainees()}
            {activeSection === 'bulk-upload' && renderBulkUpload()}
            {activeSection === 'companies' && renderCompanies()}
            {activeSection === 'events' && renderEvents()}
            {activeSection === 'analytics' && renderAnalytics()}
            {activeSection === 'settings' && renderSettings()}
          </div>
        </div>
      </div>
    </>
  );
};

export default InstituteDashboard;
